import { useInternationalization, useLocalization } from '@progress/kendo-react-intl';
import { dateFormats, datetimeFormats } from 'locales';
import { LOCALE_TYPE } from 'locales/locale';
import { LocaleType } from 'types/constants/locale';
import { GadgetCommonModelGadgetTypeCodeEnum } from 'types/entity';

export const useIntl = () => {
  const localization = useLocalization();
  const internationalization = useInternationalization();

  /**
   * 現在のロケールを取得する
   */
  const locale: LocaleType = localization.language as LocaleType || LOCALE_TYPE.JA;

  /**
   * 現在のロケールのメッセージを取得する
   */
  const formatMessage = (key: string, values?: string[]): string => {
    const localized: string = localization.toLanguageString(key, '');
    const formatted: string = internationalization.format(localized, values);
    return formatted;
  };

  /**
   * 文字列をDate型に変換する
   */
  const parseDate = (value: string) => internationalization.parseDate(value);

  /**
   * 指定されたフォーマットでDate型をフォーマットする
   */
  const formatDate = (value: Date, format: string) => internationalization.formatDate(value, format);

  /**
   * 日付のフォーマットを取得する
   */
  const dateFormat = () => dateFormats[locale];

  /**
   * 日時のフォーマットを取得する
   */
  const datetimeFormat = () => datetimeFormats[locale];

  /**
   * BFFと通信するフォーマットを取得する
   */
  const datetimeFormatWithBFF = 'yyyy-MM-ddTHH:mm:ss';

  /**
   * 表示名を取得する
   */
  const displayName = (ja: string, en: string): string => (locale === LOCALE_TYPE.EN ? en : ja);

  /**
   * 文字列型の日時データを日付フォーマットでフォーマットする
   * @param value 文字列型の日時データ
   * @returns フォーマットした日付
   */
  const parseAndFormatDate = (value: string) => formatDate(parseDate(value), dateFormat());

  /**
   * 文字列型の日時データを日時フォーマットでフォーマットする
   * @param value 文字列型の日時データ
   * @returns フォーマットした日時
   */
  const parseAndFormatDatetime = (value: string) => formatDate(parseDate(value), datetimeFormat());

  /**
   * 文字列型の日時データをBFFと通信できる日時フォーマットでフォーマットする
   * @param value 文字列型の日時データ
   * @returns フォーマットした日時
   */
  const parseAndFormatDatetimeWithBFF = (value: string) => formatDate(parseDate(value), datetimeFormatWithBFF);

  /**
   * ガジェットタイプ名称についてロケールに応じた値を返す
   * @param code
   */
  const gadgetType = (code: GadgetCommonModelGadgetTypeCodeEnum): string => {
    const key = `label.gadget-type.${code}`;
    return localization.toLanguageString(key, '');
  };

  return {
    locale,
    formatMessage,
    parseDate,
    dateFormat,
    datetimeFormat,
    displayName,
    parseAndFormatDate,
    parseAndFormatDatetime,
    parseAndFormatDatetimeWithBFF,
    gadgetType,
  };
};
